/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import l10n from '../../shared/strings';
import showBatchOperationCompletedToast from '../batchOperations/helpers/showBatchOperationCompletedToast';
import showBatchOperationStartedToast from '../batchOperations/helpers/showBatchOperationStartedToast';
import { OPERATION_TYPES, RESULT } from '../batchOperations/constants';
import { pruneSelection } from './helpers/applySelection';
import { syncProductHasVariants } from './helpers/productVariantsHelpers';
import { buildSelectionKey } from './helpers/selectionKey';
import handleStoreDisconnectedError, {
  isStoreDisconnectedError,
} from './helpers/handleStoreDisconnectedError';

const strings = l10n.ACTIONS_TOOLBAR.PRODUCTS;

const isInFlightBatch = (data) => {
  if (!data?.batchId) return false;
  if (data.status === RESULT.queued || data.status === RESULT.running) return true;
  return Number(data.queued) > 0;
};

const selectionCount = (products, variants, productIds, variantIds) => (
  (Number(products) || 0) + (Number(variants) || 0)
  || productIds.length + variantIds.length
);

const notifyDeleteResult = (data, operationType, total) => {
  if (!data) return;
  if (isInFlightBatch(data)) {
    showBatchOperationStartedToast({
      batchId: data.batchId,
      status: data.status || RESULT.queued,
      total,
      operationType: data.operationType || operationType,
    });
    return;
  }
  showBatchOperationCompletedToast({
    data: {
      batchId: data.batchId,
      operationType: data.operationType || operationType,
      total,
      succeeded: total,
      warnings: data.warnings,
      failed: data.failed,
      skipped: data.skipped,
    },
  }, { fromSyncResponse: true });
};

export const deleteMasterRecords = createAsyncThunk(
  'products/deleteMasterRecords',
  async ({
    productIds = [],
    variantIds = [],
    listingIds = [],
    selectionProducts,
    selectionVariants,
  }, {
    extra: {
      sdk, Toast, Logger,
    },
  }) => {
    try {
      let productsResult = null;
      let variantsResult = null;
      const errors = [];
      const total = selectionCount(
        selectionProducts,
        selectionVariants,
        productIds,
        variantIds,
      );

      if (productIds.length) {
        try {
          const { data } = await sdk.products.deleteMasterProducts({
            productIds,
            ...(listingIds.length && { listingIds }),
          });
          productsResult = data;
        } catch (error) {
          errors.push(error);
        }
      }

      if (variantIds.length) {
        try {
          const { data } = await sdk.products.deleteMasterVariants({
            variantIds,
            ...(listingIds.length && { listingIds }),
          });
          variantsResult = data;
        } catch (error) {
          errors.push(error);
        }
      }

      const results = [productsResult, variantsResult].filter(Boolean);
      const inFlight = results.filter(isInFlightBatch);
      if (inFlight.length) {
        inFlight.forEach((data) => {
          notifyDeleteResult(
            data,
            data === productsResult
              ? OPERATION_TYPES.DELETE_MASTER_AND_CHANNEL_RECORDS
              : OPERATION_TYPES.DELETE_VARIANTS_AND_CHANNEL_RECORDS,
            total,
          );
        });
      } else if (results.length) {
        notifyDeleteResult(
          results[0],
          productsResult
            ? OPERATION_TYPES.DELETE_MASTER_AND_CHANNEL_RECORDS
            : OPERATION_TYPES.DELETE_VARIANTS_AND_CHANNEL_RECORDS,
          total,
        );
      }

      if (errors.length && !productsResult && !variantsResult) {
        throw errors[0];
      }

      if (errors.length) {
        const disconnected = errors.find(isStoreDisconnectedError);
        if (disconnected) {
          handleStoreDisconnectedError({ Toast, error: disconnected });
        } else {
          Toast(strings.deleteFailed, { type: 'error', autoClose: false });
        }
        Logger.error(new Error('Bulk delete master records failed'), {
          error: errors[0],
        }, [
          'DeleteMasterRecordsFailed',
          { errorMessage: errors[0]?.message || 'NoMessage' },
        ]);
      }

      return {
        productIds,
        variantIds,
        productsResult,
        variantsResult,
        removedProductIds: productsResult ? productIds : [],
        removedVariantIds: variantsResult ? variantIds : [],
      };
    } catch (error) {
      if (isStoreDisconnectedError(error)) {
        handleStoreDisconnectedError({ Toast, error });
      } else {
        Toast(strings.deleteFailed, { type: 'error', autoClose: false });
      }
      Logger.error(new Error('Bulk delete master records failed'), { error }, [
        'DeleteMasterRecordsFailed',
        { errorMessage: error?.message || 'NoMessage' },
      ]);
      throw error;
    }
  },
);

export const reducer = (builder) => {
  builder.addCase(deleteMasterRecords.fulfilled, (state, { payload }) => {
    const {
      removedProductIds = [],
      removedVariantIds = [],
    } = payload;
    const removedProductSet = new Set(removedProductIds.map(String));
    const removedVariantSet = new Set(removedVariantIds.map(String));

    if (!removedProductSet.size && !removedVariantSet.size) return;

    if (removedProductSet.size) {
      state.items = state.items.filter(({ _id }) => !removedProductSet.has(String(_id)));
    }

    if (removedVariantSet.size) {
      state.items.forEach((product) => {
        if (!product.variants) return;

        const prevLength = product.variants.length;
        product.variants = product.variants.filter(
          (variant) => !removedVariantSet.has(String(variant._id)),
        );
        if (product.variants.length !== prevLength) {
          syncProductHasVariants(product);
        }
      });
    }

    const prevLength = state.search.items.length;
    state.search.items = state.search.items.filter((item) => {
      const id = String(item._id);
      const base = item.baseProductId != null ? String(item.baseProductId) : '';
      const master = item.masterProductId != null ? String(item.masterProductId) : '';
      if (removedVariantSet.has(id)) return false;
      if (removedProductSet.has(id) || removedProductSet.has(base) || removedProductSet.has(master)) {
        return false;
      }
      return true;
    });
    const removedCount = prevLength - state.search.items.length;
    if (removedCount > 0) {
      state.search.total = Math.max(0, (state.search.total || 0) - removedCount);
    }

    const keysToRemove = state.ui.selection.keys.filter((key) => {
      const entry = state.ui.selection.byKey[key];
      if (!entry) return true;
      if (removedVariantSet.has(String(entry._id))) return true;
      if (removedProductSet.has(String(entry._id))) return true;
      if (entry.type === 'masterVariant' && removedProductSet.has(String(entry.baseProductId))) {
        return true;
      }
      return false;
    });

    const remainingKeys = new Set(state.search.items.map(buildSelectionKey));
    const staleKeys = state.ui.selection.keys.filter((key) => !remainingKeys.has(key));
    state.ui.selection = pruneSelection(state.ui.selection, [...new Set([...keysToRemove, ...staleKeys])]);
    state.error = null;
  });
};
